import InviteMembersModal from "@/src/components/action-modals/add-members/add-members-modal";
import GroupDetailHeader from "@/src/components/groups/group-detail-header";
import GroupInfoSidebar from "@/src/components/groups/group-info-sidebar";
import GroupPostsFeed from "@/src/components/groups/group-posts-feed";
import TabPlaceholder from "@/src/components/groups/tab-placeholder";
import ScButton from "@/src/components/ui-kit/button";
import ToolbarCreateButton from "@/src/components/ui-kit/toolbar-create-button";
import ScToast from "@/src/components/ui-kit/toast";
import { GroupDetailsTab } from "@/src/constants/groups/group-tabs";
import { MOCK_GROUP_POSTS } from "@/src/constants/groups/mock-groups";
import { useAuth } from "@/src/contexts/auth.context";
import { useGroupDetails } from "@/src/hooks/features/groups/use-group-details";
import { useResponsiveGap } from "@/src/hooks/use-responsive-gap";
import { useLocalSearchParams, useRouter } from "expo-router";
import { AddSquare, ArrowLeft } from "iconsax-react-nativejs";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, ScrollView, View, Pressable, Text } from "react-native";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
import GroupMembersTable from "./group-members";
import { useGetPostsQuery } from "@/src/services/api/posts/posts.hooks";
import { useGetEventsQuery } from "@/src/services/api/events/events.hooks";
import { GetEventsResponse } from "@/src/models/event.model";
import GroupEvents from "@/src/components/groups/group-events";
import { isImageFile, isVideoFile } from "@/src/utils/attachment.utils";
import { Attachment } from "@/src/components/ui-kit/attachment-viewer";
import GroupFiles from "@/src/components/groups/group-files";
import { useTheme } from "@/src/styles/theme-provider";
import FeaturedNewsCarousel from "@/src/components/news/featured-news/featured-news-carousel";
import { useLatestNews } from "@/src/hooks/features/news/use-latest-news";
import AllNewsGrid from "@/src/components/news/all-news/all-news-grid";
import CategoryTags from "@/src/components/news/category-tags";
import PermissionDenied from "@/src/components/ui-kit/permission-denied";

const GroupDetailScreen = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const gap = useResponsiveGap();
  const { user } = useAuth();
  const { themeVars } = useTheme();
  //Read tab params from notification navigation
  const { id, tab, membersTab } = useLocalSearchParams<{
    id: string;
    tab?: string;
    membersTab?: string;
  }>();

  const groupDetails = useGroupDetails(id, user!);

  const { data: postsData } = useGetPostsQuery(id);
  const allPosts = postsData?.pages.flatMap(
    (page) => page?.getposts?.items ?? []
  ) ?? [];

  const groupFiles: Attachment[] = allPosts
    .flatMap((post) => post.attachmentUrls ?? [])
    .filter((url: string) => !isImageFile(url) && !isVideoFile(url))
    .map((url: string) => ({
      url,
      type: "file" as const,
    }));



  // Auto-switch to Members tab (and Pending sub-tab) when coming from join_request notification
  useEffect(() => {
    if (tab === "members") {
      groupDetails.handleTabChange(GroupDetailsTab.MEMBERS);
    }
  }, [tab]);


  const {
    searchText,
    setDeleteTarget,
    activeNews,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSaved,
    isOwner,
    toggleSave,
    togglePin,
    handleEdit,
    resolveArticleCategoryNames,
  } = useLatestNews({groupId: id});

  const renderTabContent = () => {
    // One gate for the whole screen (see `canViewContent`). Each tab used to
    // test membership for itself, and News never did — which is how a
    // non-member could read a group's news with every other tab blank. A
    // super-admin passes this gate without joining.
    const canView = groupDetails.canViewContent;

    // Blank was the old answer for a non-member, which reads as a broken tab.
    // Say why there is nothing here and what to do about it.
    if (!canView) {
      return (
        <PermissionDenied
          titleKey="LABEL_GROUP_MEMBERS_ONLY_TITLE"
          messageKey="LABEL_GROUP_MEMBERS_ONLY_DESC"
        />
      );
    }

    switch (groupDetails.activeTab) {
      case GroupDetailsTab.POSTS:
        // `id` is the whole contract: GroupPostsFeed runs its own `usePosts`
        // and owns its posts, edit modal and delete confirm. The `posts`,
        // `onEditPost` and `onDeletePost` that used to be passed here were
        // dropped on the floor — the component declares none of them — so the
        // edit and delete paths they pointed at were unreachable. Passing them
        // only implied this screen drove the feed, which it does not.
        return <GroupPostsFeed id={id} />;
      case GroupDetailsTab.MEMBERS:
        return (
          <GroupMembersTable
            groupId={id}
            isMember={groupDetails.isMember}
            isAdmin={groupDetails.isAdmin}
            addMembership={groupDetails.addMembership}
            handleRemoveMembership={groupDetails.handleRemoveMembership}
            isActionPending={groupDetails.isActionPending}
            onAdd={() => groupDetails.setMembersModalVisible(true)}
            initialMembersTab={membersTab}
          />
        );
      case GroupDetailsTab.EVENTS:
        return <GroupEvents groupId={id} />;
      case GroupDetailsTab.FILES:
        return <GroupFiles groupFiles={groupFiles} />;
      case GroupDetailsTab.NEWS:
        return (
          <View className="flex-col gap-2">
            <FeaturedNewsCarousel groupId={id} />
            <View className="flex-1 flex-col md:border md:border-ash-3 md:bg-chalk md:rounded-2xl md:p-4">
              <View className="hidden md:flex flex-row items-center justify-between mb-4">
                <Text className="text-sm font-bold text-onyx">
                  {t("LABEL_LATEST_NEWS")}
                </Text>
              </View>
              <AllNewsGrid
                articles={activeNews}
                isLoading={isLoading}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                searchText={searchText}
                variant="all"
                isSaved={isSaved}
                isOwner={isOwner}
                resolveArticleCategoryNames={resolveArticleCategoryNames}
                onSave={(itemId) => toggleSave(itemId)}
                onEdit={handleEdit}
                onDelete={(newsItem) => setDeleteTarget(newsItem)}
                onEndReached={fetchNextPage}
                onPin={(newsItem) => togglePin(newsItem)}
              />
            </View>
          </View>
        );
      default:
        return <TabPlaceholder />;
    }
  };

  return (
    <View
      className={`flex-1 flex-row w-full bg-obsidian ${gap}`}
    >
      {/* ── Main column ── */}
      <ScrollView
        className="w-full md:w-[68%]"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="grow flex-col gap-4 pb-6"
      >
        <View className="bg-chalk p-3 rounded-xl border border-ash-3 flex-row items-center justify-between">
          <Pressable
            onPress={() => router.back()}
            className="flex-row items-center gap-1 px-2 py-1 hover:bg-ash-3 hover:rounded-lg"
          >
            <ArrowLeft size={18} color={themeVars["--color-ash-1"] as string} />
            <Text className="font-medium text-onyx">{t("BTN_BACK")}</Text>
          </Pressable>
        </View>

        {/* Cover + avatar + tabs */}
        {groupDetails.isPending ? (
          <ActivityIndicator />
        ) : groupDetails.group ? (
          <GroupDetailHeader
            group={groupDetails.group}
            activeTab={groupDetails.activeTab}
            onTabChange={groupDetails.handleTabChange}
            isAdmin={groupDetails.isAdmin}
            isMember={groupDetails.isMember}
            status={groupDetails.status}
            onJoin={groupDetails.handleJoinGroup}
            actionPending={groupDetails.isActionPending}
            onLeave={groupDetails.handleRemoveMembership}
            onDeleteGroup={groupDetails.handleDeleteGroup}
            membershipId={groupDetails.myMembership?.ItemId}
          />
        ) : null}

        {groupDetails.toast && (
          <View className="absolute top-0 left-0 right-0 z-50 items-center">
            <ScToast
              text1={groupDetails.toast.text1}
              type={groupDetails.toast.type}
              onClose={() => groupDetails.setToast(null)}
            />
          </View>
        )}

        {/* Tab body */}
        <View className="flex-1">{renderTabContent()}</View>
      </ScrollView>

      {/* Right sidebar */}
      <View className="hidden md:flex md:w-[26%]">
        {groupDetails.isPending ? (
          <ActivityIndicator />
        ) : groupDetails.group ? (
          <GroupInfoSidebar
            group={groupDetails.group}
            latestGroups={groupDetails.latestGroups}
            onSelectGroup={groupDetails.handleSuggestion}
            isAdmin={groupDetails.isAdmin}
          />
        ) : null}
      </View>

      <InviteMembersModal
        members={groupDetails.invitedMembers}
        existingMemberIds={groupDetails.existingMemberIds}
        visible={groupDetails.membersModalVisible}
        onClose={() => groupDetails.setMembersModalVisible(false)}
        onInvite={(members) => groupDetails.handleAddMembers(members)}
      />

    </View>
  );
};

export default GroupDetailScreen;
